import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaCopy,
  FaUserTie,
  FaUser,
  FaUserShield,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaSync,
  FaEnvelope,
  FaPhoneAlt,
  FaIdCard,
  FaFileExcel,
  FaUpload,
  FaDownload,
  FaEye,
  FaEyeSlash,
  FaExclamationTriangle,
  FaTimesCircle,
  FaInfoCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import {
  getStudents,
  deleteStudents,
  importExcelStudents,
} from "../../services/studentService";
import { getLoggedInUser } from "../../utils/auth";

import { getClasses } from "../../services/classService";
import "../teacher/TeacherLayer.css";

function StudentLayerList() {
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Filter States
  const [search, setSearch] = useState("");
  const [classId, setClassId] = useState("");
  const [gender, setGender] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [fromFocused, setFromFocused] = useState(false);
  const [toFocused, setToFocused] = useState(false);
  
  // Pagination States
  const [page, setPage] = useState(1);
  const [limit] = useState(10);

  // Debounce search state
  const [searchTerm, setSearchTerm] = useState("");

  const [expandedRows, setExpandedRows] = useState({});

  // Excel Modal States
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelUploading, setExcelUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const fileInputRef = useRef(null);

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);
      if (search) params.append("search", search);
      if (classId) params.append("class_id", classId);
      if (gender) params.append("gender", gender);
      if (status) params.append("status", status);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);

      const query = `?${params.toString()}`;

      const res = await getStudents(query);

      if (res?.status) {
        setStudents(res.data || []);
        setTotalCount(res.count || res.total || 0);
      } else {
        setStudents([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.log(error);
      setStudents([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, classId, gender, status, fromDate, toDate]);

  const fetchClasses = async () => {
    try {
      const res = await getClasses();
      if (res?.status) setClasses(res.data || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [search, classId, gender, status, fromDate, toDate]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this student?"
    );

    if (!confirmDelete) return;

    const res = await deleteStudents(id);

    if (res?.status) {
      toast.success(res?.message || "Student deleted successfully");
      fetchStudents();
    } else {
      toast.error(res?.message || "Delete failed");
    }
  };

  const copyData = (student) => {
    const text = `Email: ${student.email}\nPassword: ${student.plain_password || ""}`;
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const handleExcelFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setExcelUploading(true);

    try {
      const user = getLoggedInUser();
      const schoolId = user?.school_id || user?.id || user?.user_id || 131;

      const formData = new FormData();
      formData.append("file", file);
      if (schoolId) {
        formData.append("school_id", schoolId);
      }

      const res = await importExcelStudents(formData);

      const isSuccess =
        res?.status === true ||
        res?.status === "true" ||
        res?.status === 1 ||
        res?.status === "1" ||
        res?.status === "success";

      const importedCount = res?.summary?.imported ?? (res?.imported_students?.length ?? 0);
      const skippedCount = res?.summary?.skipped ?? (res?.skipped_students?.length ?? 0);

      setImportResult(res);

      if (isSuccess) {
        if (importedCount > 0) {
          fetchStudents();
        }

        if (skippedCount > 0 && res?.skipped_students && res.skipped_students.length > 0) {
          setShowExcelModal(false);
          setShowResultModal(true);
          if (importedCount > 0) {
            toast.warning(res?.message || `${importedCount} student(s) imported, ${skippedCount} skipped.`);
          } else {
            toast.error(res?.message || `0 student(s) imported. ${skippedCount} skipped.`);
          }
        } else {
          toast.success(res?.message || `${importedCount} student(s) imported successfully!`);
          setShowExcelModal(false);
        }
      } else {
        if (res?.skipped_students && res.skipped_students.length > 0) {
          setShowExcelModal(false);
          setShowResultModal(true);
        }
        toast.error(res?.message || "Excel import failed on server.");
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to upload and import file.");
    } finally {
      setExcelUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const downloadSampleTemplate = () => {
    const fileUrl = "/assets/files/valid_students_sample.csv";
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "valid_students_sample.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / limit));

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          
          <div className="admin-page-header list-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
              <h6 className="fw-bold text-primary mb-0 d-flex align-items-center gap-2 text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '15px' }}>
                <div style={{ width: '6px', height: '18px', backgroundColor: '#4f46e5', borderRadius: '3px' }}></div>
                Student Management
              </h6>

            <div className="d-flex flex-wrap align-items-center gap-2">
              <button 
                className="btn btn-success text-white fw-semibold d-inline-flex align-items-center gap-2 rounded-pill px-3 py-2 shadow-sm"
                onClick={() => setShowExcelModal(true)}
              >
                <FaFileExcel /> Import Excel
              </button>
              <button className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle shadow-sm" onClick={fetchStudents} style={{ width: '40px', height: '40px' }} title="Refresh">
                <FaSync className="text-secondary" />
              </button>
              <Link
                to="/add-student-page"
                className="btn-primary text-decoration-none d-inline-flex align-items-center gap-2"
                style={{ lineHeight: "1" }}
              >
                <FaPlus /> Add Student
              </Link>
            </div>
          </div>

          <div className="bg-light p-3 rounded-3 mb-4 border student-filter-card" style={{ borderColor: '#e9ecef' }}>
            <h6 className="mb-3 d-flex align-items-center gap-2 text-secondary">
              <FaFilter /> Advanced Filters
            </h6>
            
            {/* ROW 1 */}
            <div className="row g-3 align-items-end mb-3">
              <div className="col-12 col-md-6 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Search</label>
                <div className="admin-search search-box m-0 w-100">
                  <input
                    type="text"
                    className="form-control text-center"
                    placeholder="Name, Roll No, Email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ height: '42px', borderRadius: '8px' }}
                  />
                </div>
              </div>

              <div className="col-12 col-md-6 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Class</label>
                <select
                  className="form-select w-100 text-center"
                  style={{ height: '42px', borderRadius: '8px' }}
                  value={classId}
                  onChange={(e) => setClassId(e.target.value)}
                >
                  <option value="">All Classes</option>
                  {classes.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.class_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-4 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Gender</label>
                <select
                  className="form-select w-100 text-center"
                  style={{ height: '42px', borderRadius: '8px' }}
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="col-12 col-md-4 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>Status</label>
                <select
                  className="form-select w-100 text-center"
                  style={{ height: '42px', borderRadius: '8px' }}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            {/* ROW 2 */}
            <div className="row g-3 align-items-end">
              <div className="col-12 col-md-4 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>From Date</label>
                <input
                  type={fromFocused || fromDate ? "date" : "text"}
                  className="form-control text-center"
                  placeholder="Created From"
                  value={fromDate}
                  onFocus={() => setFromFocused(true)}
                  onBlur={() => setFromFocused(false)}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{ height: '42px', borderRadius: '8px' }}
                />
              </div>

              <div className="col-12 col-md-4 col-lg-3">
                <label className="fw-semibold text-muted d-block mb-1" style={{ fontSize: '13px' }}>To Date</label>
                <input
                  type={toFocused || toDate ? "date" : "text"}
                  className="form-control text-center"
                  placeholder="Created To"
                  value={toDate}
                  onFocus={() => setToFocused(true)}
                  onBlur={() => setToFocused(false)}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{ height: '42px', borderRadius: '8px' }}
                />
              </div>

              <div className="col-12 col-md-4 col-lg-6 d-flex align-items-center justify-content-md-end">
                {(search || classId || gender || status || fromDate || toDate) && (
                  <button
                    className="btn btn-outline-secondary btn-sm px-3 rounded-pill"
                    style={{ height: '38px' }}
                    onClick={() => {
                      setSearchTerm("");
                      setSearch("");
                      setClassId("");
                      setGender("");
                      setStatus("");
                      setFromDate("");
                      setToDate("");
                    }}
                  >
                    Reset Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="teacher-table-shell admin-table-card admin-table-scroll admin-responsive-table border rounded-3 p-2 shadow-sm mb-4">
            <table className="user-table align-middle text-nowrap">
              <thead>
                <tr>
                  <th>#</th>
                  <th>ID Card</th>
                  <th>Student Info</th>
                  <th>Class & Roll</th>
                  <th>Family Details</th>
                  <th>Mobile / Contact</th>
                  <th>Status</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted p-4">Loading students...</td>
                  </tr>
                ) : students.length > 0 ? (
                  students.map((item, index) => {
                    const isExpanded = !!expandedRows[item.id];
                    const parentEmail = item.email || "-";
                    const isGuardian = Boolean(item.guardian_name) && !item.father_name && !item.mother_name;

                    return (
                      <React.Fragment key={item.id}>
                        <tr className="smooth-table-row">
                          <td>{index + 1 + (page - 1) * limit}</td>
                          <td>
                            <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2 py-1 font-monospace">
                              <FaIdCard className="me-1" />
                              {item.id_card_code || item.id_card_number || "N/A"}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {item.student_photo ? (
                                <img
                                  src={item.student_photo.startsWith("data:") || item.student_photo.startsWith("http") ? item.student_photo : `/uploads/${item.student_photo}`}
                                  alt={item.name}
                                  className="rounded-circle border shadow-sm"
                                  style={{ width: "36px", height: "36px", objectFit: "cover" }}
                                  onError={(e) => { e.target.style.display = "none"; }}
                                />
                              ) : null}
                              <div className="bg-primary bg-opacity-10 text-primary fw-bold rounded-circle d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', display: item.student_photo ? 'none' : 'flex' }}>
                                {(item.name || "?").charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="fw-bold text-dark">{item.name}</div>
                                <div className="small text-muted">{item.gender || "Gender N/A"}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="fw-semibold text-dark">{item.class_name || `Class #${item.class_id}`}</div>
                            <div className="small text-secondary">Roll: {item.roll_number || "-"}</div>
                          </td>
                          <td>
                            {isGuardian ? (
                              <div>
                                <div className="small fw-semibold text-dark"><FaUserShield className="me-1 text-warning" />{item.guardian_name}</div>
                                <div className="small text-muted">Guardian</div>
                              </div>
                            ) : (
                              <div>
                                {item.father_name && <div className="small fw-semibold text-dark"><FaUserTie className="me-1 text-primary" />{item.father_name}</div>}
                                {item.mother_name && <div className="small text-secondary"><FaUser className="me-1 text-info" />{item.mother_name}</div>}
                                {!item.father_name && !item.mother_name && <div className="small text-muted">-</div>}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="small fw-semibold text-dark"><FaPhoneAlt className="me-1 text-success" />{item.mobile || "-"}</div>
                            <div className="small text-muted text-truncate" style={{ maxWidth: '150px' }}><FaEnvelope className="me-1" />{parentEmail}</div>
                          </td>
                          <td>
                            <span className={`badge rounded-pill px-3 py-1 ${item.status === 'active' ? 'bg-success bg-opacity-10 text-success border border-success' : 'bg-danger bg-opacity-10 text-danger border border-danger'}`}>
                              {item.status || "active"}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="d-flex align-items-center justify-content-end gap-1">
                              <button
                                className={`btn btn-sm border ${isExpanded ? 'btn-primary text-white' : 'btn-light text-info'}`}
                                onClick={() => toggleRow(item.id)}
                                title={isExpanded ? "Collapse Details" : "View All Student Details"}
                              >
                                {isExpanded ? <FaEyeSlash /> : <FaEye />}
                              </button>
                              <button
                                className="btn btn-sm btn-light border text-secondary"
                                onClick={() => copyData(item)}
                                title="Copy Login Data"
                              >
                                <FaCopy />
                              </button>
                              <Link
                                to={`/edit-student-page/${item.id}`}
                                className="btn btn-sm btn-light border text-primary"
                                title="Edit Student"
                              >
                                <FaEdit />
                              </Link>
                              <button
                                className="btn btn-sm btn-light border text-danger"
                                onClick={() => handleDelete(item.id)}
                                title="Delete Student"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expand View Single Student All Details */}
                        {isExpanded && (
                          <tr key={`exp-${item.id}`} className="bg-light">
                            <td colSpan="8" className="p-3">
                              <div className="card border-0 shadow-sm rounded-4 p-3 bg-white">
                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 border-bottom pb-3 mb-3">
                                  <div className="d-flex align-items-center gap-3">
                                    {item.student_photo ? (
                                      <img
                                        src={item.student_photo.startsWith("data:") || item.student_photo.startsWith("http") ? item.student_photo : `/uploads/${item.student_photo}`}
                                        alt={item.name}
                                        className="rounded-circle border shadow-sm"
                                        style={{ width: "64px", height: "64px", objectFit: "cover" }}
                                        onError={(e) => { e.target.src = "https://via.placeholder.com/64?text=Photo"; }}
                                      />
                                    ) : (
                                      <div className="bg-primary bg-opacity-10 text-primary fw-bold rounded-circle d-flex align-items-center justify-content-center" style={{ width: "64px", height: "64px", fontSize: "26px" }}>
                                        {(item.name || "?").charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    <div>
                                      <h5 className="fw-bold mb-1 text-dark">{item.name}</h5>
                                      <div className="d-flex flex-wrap gap-2">
                                        <span className="badge bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25 px-2.5 py-1">
                                          ID Card: {item.id_card_code || item.id_card_number || "N/A"}
                                        </span>
                                        <span className="badge bg-secondary bg-opacity-10 text-secondary border border-secondary border-opacity-25 px-2.5 py-1">
                                          Roll: {item.roll_number || "N/A"}
                                        </span>
                                        <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25 px-2.5 py-1">
                                          Class: {item.class_name || `Class #${item.class_id}`} ({item.section || 'A'})
                                        </span>
                                        <span className={`badge px-2.5 py-1 ${item.status === 'active' ? 'bg-success bg-opacity-10 text-success border border-success' : 'bg-danger bg-opacity-10 text-danger border border-danger'}`}>
                                          Status: {item.status || "active"}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary rounded-pill px-3"
                                    onClick={() => toggleRow(item.id)}
                                  >
                                    Close Details
                                  </button>
                                </div>

                                <div className="row g-3" style={{ fontSize: "13px" }}>
                                  <div className="col-12 col-sm-6 col-md-3">
                                    <div className="text-muted fw-semibold small text-uppercase">Registration No</div>
                                    <div className="fw-bold text-dark">{item.reg_number || item.reg_no || "N/A"}</div>
                                  </div>
                                  <div className="col-12 col-sm-6 col-md-3">
                                    <div className="text-muted fw-semibold small text-uppercase">Blood Group</div>
                                    <div className="fw-bold text-danger">{item.blood_group || "N/A"}</div>
                                  </div>
                                  <div className="col-12 col-sm-6 col-md-3">
                                    <div className="text-muted fw-semibold small text-uppercase">Gender & DOB</div>
                                    <div className="fw-bold text-dark">{item.gender || "Male"} | {item.birthdate || "N/A"}</div>
                                  </div>
                                  <div className="col-12 col-sm-6 col-md-3">
                                    <div className="text-muted fw-semibold small text-uppercase">Academic Year</div>
                                    <div className="fw-bold text-primary">{item.academic_year || "2024-2025"}</div>
                                  </div>

                                  <div className="col-12 col-sm-6 col-md-3">
                                    <div className="text-muted fw-semibold small text-uppercase">Mobile Number</div>
                                    <div className="fw-bold text-dark">{item.mobile || "N/A"}</div>
                                  </div>
                                  <div className="col-12 col-sm-6 col-md-3">
                                    <div className="text-muted fw-semibold small text-uppercase">Email Address</div>
                                    <div className="fw-bold text-dark text-truncate">{item.email || "N/A"}</div>
                                  </div>
                                  <div className="col-12 col-sm-6 col-md-3">
                                    <div className="text-muted fw-semibold small text-uppercase">Account Password</div>
                                    <div className="fw-bold text-secondary font-monospace">{item.plain_password || item.password || "N/A"}</div>
                                  </div>
                                  <div className="col-12 col-sm-6 col-md-3">
                                    <div className="text-muted fw-semibold small text-uppercase">Photo Filename</div>
                                    <div className="fw-bold text-truncate">{item.student_photo || "devav.png"}</div>
                                  </div>

                                  <div className="col-12 col-md-6">
                                    <div className="text-muted fw-semibold small text-uppercase">Family Details</div>
                                    <div className="fw-medium text-dark">
                                      {item.father_name && <span>Father: <strong>{item.father_name}</strong> | </span>}
                                      {item.mother_name && <span>Mother: <strong>{item.mother_name}</strong> | </span>}
                                      {item.guardian_name && <span>Guardian: <strong>{item.guardian_name}</strong></span>}
                                      {!item.father_name && !item.mother_name && !item.guardian_name && <span>N/A</span>}
                                    </div>
                                  </div>

                                  <div className="col-12 col-md-6">
                                    <div className="text-muted fw-semibold small text-uppercase">Residential Address</div>
                                    <div className="fw-medium text-dark">{item.address || "N/A"}</div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center text-muted p-4">No students found matching your criteria.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {students.length > 0 && (
            <div className="admin-pagination mt-4">
              <div className="admin-pagination-info">
                Showing page {page} {totalCount > 0 && `of ${totalPages} (${totalCount} total students)`}
              </div>

              <div className="admin-pagination-controls d-flex flex-wrap justify-content-center gap-1">
                <button
                  type="button"
                  className="admin-page-btn"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page === 1}
                >
                  <FaChevronLeft />
                </button>

                {totalCount > 0 ? (
                  Array.from({ length: Math.min(5, totalPages) }, (_, index) => {
                    let startPage = Math.max(1, page - 2);
                    if (startPage + 4 > totalPages) {
                      startPage = Math.max(1, totalPages - 4);
                    }
                    const pageNum = startPage + index;
                    
                    if (pageNum > totalPages) return null;

                    return (
                      <button
                        type="button"
                        key={pageNum}
                        className={`admin-page-btn ${page === pageNum ? "active" : ""}`}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  })
                ) : (
                  <button type="button" className="admin-page-btn active">
                    {page}
                  </button>
                )}

                <button
                  type="button"
                  className="admin-page-btn"
                  onClick={() => setPage((prev) => (totalCount > 0 ? Math.min(totalPages, prev + 1) : prev + 1))}
                  disabled={totalCount > 0 && page === totalPages}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}

          {/* Excel Import Modal */}
          {showExcelModal && createPortal(
            <div 
              className="modal fade show d-block" 
              style={{ 
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(15, 23, 42, 0.75)", 
                backdropFilter: "blur(6px)", 
                zIndex: 999999,
                overflowX: "hidden",
                overflowY: "auto"
              }}
              tabIndex="-1"
              onClick={(e) => {
                if (e.target === e.currentTarget) setShowExcelModal(false);
              }}
            >
              <div className="modal-dialog modal-dialog-centered modal-md p-2 p-sm-3" style={{ margin: "1.75rem auto", maxWidth: "540px" }}>
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden" style={{ zIndex: 1000000 }}>
                  
                  {/* Header */}
                  <div 
                    className="modal-header border-0 px-3 px-sm-4 py-3 text-white d-flex align-items-center justify-content-between"
                    style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div className="p-2 rounded-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center">
                        <FaFileExcel className="fs-5 text-white" />
                      </div>
                      <div>
                        <h5 className="modal-title fw-bold text-white mb-0" style={{ fontSize: "16px", letterSpacing: "0.3px" }}>
                          Import Students from Excel
                        </h5>
                        <div className="small text-white text-opacity-75" style={{ fontSize: "11px" }}>
                          Bulk enroll students via .xlsx, .xls, or .csv
                        </div>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      className="btn-close btn-close-white shadow-none" 
                      onClick={() => setShowExcelModal(false)}
                    ></button>
                  </div>

                  {/* Body */}
                  <div className="modal-body p-3 p-sm-4 bg-white">
                    <div className="alert alert-info border-0 rounded-3 p-2.5 mb-3 d-flex align-items-start gap-2" style={{ backgroundColor: "#ecfdf5", color: "#065f46", fontSize: "12.5px" }}>
                      <FaFileExcel className="mt-1 flex-shrink-0 text-success fs-5" />
                      <div>
                        <strong>Required Headers:</strong> Each row must include 
                        <span className="badge bg-success bg-opacity-20 text-success mx-1">Name</span>, 
                        <span className="badge bg-success bg-opacity-20 text-success me-1">Mobile</span>, and 
                        <span className="badge bg-success bg-opacity-20 text-success">ID Card Code</span>.
                      </div>
                    </div>

                    {/* Upload Box */}
                    <div 
                      className="border border-2 border-dashed rounded-4 p-4 text-center mb-3 transition-all"
                      style={{ 
                        borderColor: "#10b981", 
                        backgroundColor: "#f0fdf4",
                        cursor: "pointer"
                      }}
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                      <div className="p-3 rounded-circle bg-success bg-opacity-10 text-success d-inline-flex mb-2">
                        <FaUpload className="fs-3" />
                      </div>
                      <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "15px" }}>
                        Click to browse or drop your Excel file
                      </h6>
                      <p className="text-muted small mb-3" style={{ fontSize: "12px" }}>
                        Supports Microsoft Excel (.xlsx, .xls) and CSV (.csv) files
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".xlsx, .xls, .csv"
                        className="d-none"
                        onChange={handleExcelFileSelect}
                        disabled={excelUploading}
                      />
                      <button
                        type="button"
                        className="btn btn-success btn-sm rounded-pill px-4 py-2 shadow-sm text-white fw-bold"
                        disabled={excelUploading}
                      >
                        {excelUploading ? "Uploading..." : "Select File"}
                      </button>
                    </div>

                    {excelUploading && (
                      <div className="p-3 rounded-3 bg-light border text-center my-3">
                        <div className="spinner-border spinner-border-sm text-success me-2" role="status"></div>
                        <span className="fw-semibold text-success small">Reading rows and enrolling students into system...</span>
                      </div>
                    )}

                    {/* Modal Footer Controls */}
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2 mt-4 pt-3 border-top">
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm rounded-pill d-flex align-items-center justify-content-center gap-2 px-3 py-2 fw-semibold"
                        onClick={downloadSampleTemplate}
                        style={{ fontSize: "13px" }}
                      >
                        <FaDownload /> Download Sample Template
                      </button>
                      <button
                        type="button"
                        className="btn btn-light border btn-sm rounded-pill px-4 py-2 text-secondary fw-semibold"
                        onClick={() => setShowExcelModal(false)}
                        style={{ fontSize: "13px" }}
                      >
                        Close
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Skipped Data Modal */}
          {showResultModal && importResult && createPortal(
            <div 
              className="modal fade show d-block" 
              style={{ 
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(15, 23, 42, 0.75)", 
                backdropFilter: "blur(6px)", 
                zIndex: 999999,
                overflowX: "hidden",
                overflowY: "auto"
              }}
              tabIndex="-1"
              onClick={(e) => {
                if (e.target === e.currentTarget) {
                  setShowResultModal(false);
                  setImportResult(null);
                }
              }}
            >
              <div className="modal-dialog modal-dialog-centered modal-xl p-2 p-sm-3" style={{ margin: "1.75rem auto", maxWidth: "920px" }}>
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden" style={{ zIndex: 1000000 }}>
                  
                  {/* Header */}
                  <div 
                    className="modal-header border-0 px-3 px-sm-4 py-3 text-white d-flex align-items-center justify-content-between"
                    style={{ 
                      background: (importResult?.summary?.imported || importResult?.imported_students?.length || 0) > 0 
                        ? "linear-gradient(135deg, #d97706 0%, #b45309 100%)" 
                        : "linear-gradient(135deg, #dc2626 0%, #991b1b 100%)" 
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <div className="p-2 rounded-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center">
                        <FaExclamationTriangle className="fs-5 text-white" />
                      </div>
                      <div>
                        <h5 className="modal-title fw-bold text-white mb-0" style={{ fontSize: "16px", letterSpacing: "0.3px" }}>
                          Import Results - Skipped Records
                        </h5>
                        <div className="small text-white text-opacity-90" style={{ fontSize: "11px" }}>
                          {importResult?.message || "Detailed error reasons for records that could not be imported"}
                        </div>
                      </div>
                    </div>
                    <button 
                      type="button" 
                      className="btn-close btn-close-white shadow-none" 
                      onClick={() => {
                        setShowResultModal(false);
                        setImportResult(null);
                      }}
                      title="Close"
                    ></button>
                  </div>

                  {/* Body */}
                  <div className="modal-body p-3 p-sm-4 bg-light">
                    
                    {/* Summary Cards */}
                    <div className="row g-2 mb-3">
                      <div className="col-4">
                        <div className="bg-white p-3 rounded-3 border text-center shadow-sm">
                          <div className="text-secondary small fw-semibold">Total Processed</div>
                          <div className="fs-4 fw-bold text-dark">
                            {importResult?.summary?.total ?? ((importResult?.summary?.imported || 0) + (importResult?.summary?.skipped || 0))}
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="bg-white p-3 rounded-3 border text-center shadow-sm">
                          <div className="text-success small fw-semibold">Imported Successfully</div>
                          <div className="fs-4 fw-bold text-success">
                            {importResult?.summary?.imported ?? importResult?.imported_students?.length ?? 0}
                          </div>
                        </div>
                      </div>
                      <div className="col-4">
                        <div className="bg-white p-3 rounded-3 border text-center shadow-sm">
                          <div className="text-danger small fw-semibold">Skipped Records</div>
                          <div className="fs-4 fw-bold text-danger">
                            {importResult?.summary?.skipped ?? importResult?.skipped_students?.length ?? 0}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Notice alert */}
                    <div className="alert alert-warning border-0 rounded-3 p-2.5 mb-3 d-flex align-items-center gap-2" style={{ backgroundColor: "#fffbeb", color: "#92400e", fontSize: "12.5px" }}>
                      <FaInfoCircle className="flex-shrink-0 text-warning fs-5" />
                      <div>
                        The following <strong>{importResult?.skipped_students?.length || 0} student records</strong> were skipped. Please review the reasons below, resolve any duplicates or missing fields in your file, and re-upload.
                      </div>
                    </div>

                    {/* Table of skipped records */}
                    <div className="card border-0 rounded-3 shadow-sm overflow-hidden mb-3">
                      <div className="table-responsive" style={{ maxHeight: "380px", overflowY: "auto" }}>
                        <table className="table table-hover align-middle mb-0" style={{ fontSize: "13px" }}>
                          <thead className="table-light sticky-top" style={{ zIndex: 1, backgroundColor: "#f8fafc" }}>
                            <tr>
                              <th style={{ width: "50px" }} className="text-center">Sr.</th>
                              <th style={{ minWidth: "150px" }}>Student Name</th>
                              <th style={{ minWidth: "90px" }}>Roll No</th>
                              <th style={{ minWidth: "120px" }}>ID Card Code</th>
                              <th style={{ minWidth: "110px" }}>Mobile</th>
                              <th style={{ minWidth: "260px" }}>Skipped Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(importResult?.skipped_students || []).map((stu, idx) => (
                              <tr key={idx}>
                                <td className="text-center fw-bold text-secondary">
                                  {stu.sr_no || idx + 1}
                                </td>
                                <td className="fw-semibold text-dark">
                                  {stu.name || <span className="text-muted fst-italic">Empty</span>}
                                </td>
                                <td>
                                  {stu.roll_number ? (
                                    <span className="badge bg-light text-dark border">{stu.roll_number}</span>
                                  ) : (
                                    <span className="text-muted fst-italic">-</span>
                                  )}
                                </td>
                                <td>
                                  {stu.id_card_code ? (
                                    <span className="badge bg-light text-primary border">{stu.id_card_code}</span>
                                  ) : (
                                    <span className="text-muted fst-italic">-</span>
                                  )}
                                </td>
                                <td>
                                  {stu.mobile || <span className="text-muted fst-italic">-</span>}
                                </td>
                                <td>
                                  <span className="text-danger fw-medium d-inline-flex align-items-start gap-1" style={{ fontSize: "12px", lineHeight: "1.4" }}>
                                    <FaTimesCircle className="flex-shrink-0 mt-0.5 text-danger" />
                                    <span>{stu.reason || "Validation error"}</span>
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    {/* Footer Controls */}
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center gap-2 pt-2 border-top">
                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm rounded-pill d-flex align-items-center justify-content-center gap-2 px-3 py-2 fw-semibold"
                        onClick={downloadSampleTemplate}
                        style={{ fontSize: "13px" }}
                      >
                        <FaDownload /> Download Sample Template
                      </button>
                      <div className="d-flex gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm rounded-pill px-4 py-2 fw-semibold d-flex align-items-center gap-1.5"
                          onClick={() => {
                            setShowResultModal(false);
                            setShowExcelModal(true);
                          }}
                          style={{ fontSize: "13px" }}
                        >
                          <FaUpload /> Upload Another File
                        </button>
                        <button
                          type="button"
                          className="btn btn-dark btn-sm rounded-pill px-4 py-2 text-white fw-bold shadow-sm"
                          onClick={() => {
                            setShowResultModal(false);
                            setImportResult(null);
                          }}
                          style={{ fontSize: "13px" }}
                        >
                          Close
                        </button>
                      </div>
                    </div>

                  </div>

                </div>
              </div>
            </div>,
            document.body
          )}

        </div>
      </div>
    </div>
  );
}

export default StudentLayerList;